import type { Rule } from "./form-check";

/**
 * Each form's required fields, as rules for form-check.tsx, in the order the
 * fields appear. The messages are the server's wording (checks.test.ts holds
 * them to it); the server keeps its own checks.
 */

/** cleanText's message for a required field left empty. */
export const write = (what: string) => `Write ${what}.`;
const blank = (s: string | undefined) => !(s ?? "").trim();
const PIN_RE = /^\d{4}$/;
export const VERIFIER_MISSING = "The second person enters their own email and 4-digit PIN.";
export const QUANTITY = "Enter the quantity received, as a whole number (count the doses).";
export const EXCEPTIONS = "Choose at least one exception.";
export const WINDOWS = "Choose at least one medication window for a scheduled medication.";

/** The receipt: exceptions, the count, and a controlled medication's second person. */
function receiptRules(p: string, r: { status: string; exceptions: string[]; quantity: string; controlled: boolean; vEmail: string; vPin: string }): Rule[] {
  const held = r.status === "Received" || r.status === "ReceivedWithException";
  return [
    [`${p}-x`, r.status === "ReceivedWithException" && !r.exceptions.length, EXCEPTIONS],
    [`${p}-q`, held && !/^\d{1,4}$/.test(r.quantity), QUANTITY],
    [`${p}-ve`, r.controlled && held && blank(r.vEmail), VERIFIER_MISSING],
    [`${p}-vp`, r.controlled && held && !PIN_RE.test(r.vPin), VERIFIER_MISSING],
  ];
}

// ---- Add a medication not on the list (StationAdded) --------------------------------------------

export interface AddFormState {
  photos: number;
  label: { name: string; dose: string; route: string; frequency: string; isPrn: boolean; prnInstructions: string; controlled: boolean };
  status: string;
  exceptions: string[];
  quantity: string;
  vEmail: string;
  vPin: string;
  att: { name: string; relationship: string; phone: string; agreed: boolean };
  st: { started: string; reaction: string; reactionDetail: string; newAllergy: string; newAllergyDetail: string };
}

export function addFormRules(s: AddFormState): Rule[] {
  const l = s.label;
  const self = s.att.relationship === "Self";
  const phoneDigits = s.att.phone.replace(/\D/g, "").length;
  return [
    ["add-photos", s.photos === 0, "Take at least one photo of the label first."],
    ["add-n", blank(l.name), write("the medication's name (from the label)")],
    ["add-d", blank(l.dose), write("the dose (from the label)")],
    ["add-r", blank(l.route), write("the route")],
    ["add-fr", !l.isPrn && blank(l.frequency), write("how often (from the label)")],
    ["add-prn", l.isPrn && blank(l.prnInstructions), write("when to give it (as needed)")],
    ...receiptRules("add", { status: s.status, exceptions: s.exceptions, quantity: s.quantity, controlled: l.controlled, vEmail: s.vEmail, vPin: s.vPin }),
    ["add-an", blank(s.att.name), write(self ? "the attendee's full name" : "the parent or guardian's full name")],
    ["add-ap", !self && blank(s.att.phone), write("the phone number")],
    ["add-ap", !blank(s.att.phone) && phoneDigits < 7, "Enter a phone number."],
    ["add-q1", !["New", "Established", "NotSure"].includes(s.st.started), "Answer how long the student has been taking it: New, Established or Not sure."],
    ["add-q2", s.st.reaction !== "No" && s.st.reaction !== "Yes", "Answer whether the student has had any reaction or problem taking it: No or Yes."],
    ["add-q2d", s.st.reaction === "Yes" && blank(s.st.reactionDetail), write("the reaction or problem")],
    ["add-q3", s.st.newAllergy !== "No" && s.st.newAllergy !== "Yes", "Answer whether there are medication allergies not on the application: No or Yes."],
    ["add-q3d", s.st.newAllergy === "Yes" && blank(s.st.newAllergyDetail), write("the medication allergies")],
    ["add-agreed", !s.att.agreed, "The attestation box must be ticked."],
  ];
}

// ---- One dose, or its correction (Pass DoseForm) -------------------------------------------------

export interface DoseFormState {
  correcting: boolean;
  why: string;
  disposition: string;
  amount: string;
  time: string;
  paper: boolean;
  by: string;
  reason: string;
  reasonText: string;
  hasReasons: boolean;
  isLate: boolean;
  late: string;
  reportedBy: string;
  counted: boolean;
  before: string;
  after: string;
  vEmail: string;
  vPin: string;
}

export const REASON = "A reason is required: choose one from the list (Config: Held / refused reasons) and add a note if needed.";
export const COUNTS = "A controlled medication: count before and after, as whole numbers.";

/** `id(k)` is the form's own element id for field k. */
export function doseFormRules(s: DoseFormState, id: (k: string) => string): Rule[] {
  const taken = s.disposition === "Given" || s.disposition === "SelfAdministered";
  const heldOrRefused = s.disposition === "Held" || s.disposition === "Refused";
  const count = (x: string) => /^\d{1,4}$/.test(x);
  return [
    [id("why"), s.correcting && blank(s.why), write("why this entry is being corrected")],
    [id("amt"), taken && blank(s.amount), write("the amount given")],
    [id("t"), s.paper && !s.time, "Entering from paper: type the time written on the paper."],
    [id("by"), taken && !s.by, "Choose who administered it from the list."],
    [id("r"), heldOrRefused && s.hasReasons && !s.reason, REASON],
    [id("rt"), heldOrRefused && !s.hasReasons && blank(s.reasonText), REASON],
    [id("late"), s.isLate && blank(s.late), write("why it was given after the window")],
    [id("rep"), s.disposition === "PrnNotNeeded" && blank(s.reportedBy), write("who reported it isn't needed")],
    [id("cb"), s.counted && !count(s.before), COUNTS],
    [id("ca"), s.counted && !count(s.after), COUNTS],
    [id("ca"), s.counted && count(s.before) && count(s.after) && Number(s.after) > Number(s.before), "The count after can't be more than the count before."],
    [id("ve"), s.counted && blank(s.vEmail), VERIFIER_MISSING],
    [id("vp"), s.counted && !PIN_RE.test(s.vPin), VERIFIER_MISSING],
  ];
}

// ---- Receive / confirm a parent's medication (Station MedCard) ----------------------------------

export interface MedCardState {
  receipt: boolean;
  confirm: boolean;
  status: string;
  exceptions: string[];
  quantity: string;
  controlled: boolean;
  vEmail: string;
  vPin: string;
  name: string;
  dose: string;
  route: string;
  isPrn: boolean;
  prn: string;
  windows: string[];
}

export function medCardRules(s: MedCardState, p: string): Rule[] {
  return [
    ...(s.receipt ? receiptRules(p, s) : []),
    ...(s.confirm
      ? ([
          [`${p}-n`, blank(s.name), write("the medication's name")],
          [`${p}-d`, blank(s.dose), write("the dose")],
          [`${p}-r`, blank(s.route), write("the route")],
          [`${p}-prn`, s.isPrn && blank(s.prn), write("when to give it (as needed)")],
          [`${p}-w`, !s.isPrn && !s.windows.length, WINDOWS],
        ] as Rule[])
      : []),
  ];
}

// ---- Confirm the allergies (Station) --------------------------------------------------------------

export function allergyRowsRules(rows: { allergy: string }[]): Rule[] {
  return rows.map((r, i) => [`al-a-${i}`, blank(r.allergy), write("each allergy's name")] as Rule);
}

// ---- The allergy response note -------------------------------------------------------------------

export function allergyResponseRules(f: { allergens: string; severity: string; rescueMedication: string; rescueLocation: string; steps: string }): Rule[] {
  return [
    ["ar-a", blank(f.allergens), write("the allergen(s)")],
    ["ar-s", !["Mild", "Moderate", "Anaphylaxis"].includes(f.severity), "Choose the severity: Mild, Moderate or Anaphylaxis."],
    ["ar-l", !blank(f.rescueMedication) && !f.rescueLocation, "Say where the rescue medication is: Med station, Self-carry or With counselor."],
    ["ar-l", blank(f.rescueMedication) && Boolean(f.rescueLocation), "Name the rescue medication, or leave where it is blank."],
    ["ar-st", blank(f.steps), write("the steps if exposed")],
  ];
}

// ---- An authorization decision (Path A and Path B) ------------------------------------------------

export interface DecisionState {
  decision: string;
  order: { name: string; dose: string; route: string; isPrn: boolean; prnInstructions: string; windows: string[] };
  notes: string;
}

/** `p` is the form's id prefix ("pa" or "pb"). */
export function decisionRules(s: DecisionState, p: string): Rule[] {
  const o = s.order;
  const authorized = s.decision !== "NotAuthorized";
  return [
    [`${p}-n`, authorized && blank(o.name), write("the medication's name")],
    [`${p}-d`, authorized && blank(o.dose), write("the dose")],
    [`${p}-r`, authorized && blank(o.route), write("the route")],
    [`${p}-prn`, authorized && o.isPrn && blank(o.prnInstructions), write("when to give it (as needed)")],
    [`${p}-w`, authorized && !o.isPrn && !o.windows.length, WINDOWS],
    [`${p}-no`, s.decision !== "Authorized" && blank(s.notes), write(s.decision === "NotAuthorized" ? "why it was not authorized" : "what changed")],
  ];
}

export function pathARules(s: DecisionState & { typedName: string; credential: string; drawn: string }): Rule[] {
  return [
    ...decisionRules(s, "pa"),
    ["pa-tn", blank(s.typedName), write("your full name")],
    ["pa-cr", blank(s.credential), write("your credential")],
    ["pa-sig", !s.drawn, "Draw your signature."],
  ];
}

export function pathBRules(s: DecisionState & { attached: boolean; providerName: string; providerCredential: string }): Rule[] {
  return [
    ["pb-file", !s.attached, "Attach the signed authorization PDF first."],
    ["pb-pn", blank(s.providerName), write("the provider's name (from the signed document)")],
    ["pb-pc", blank(s.providerCredential), write("the provider's credential")],
    ...decisionRules(s, "pb"),
  ];
}

// ---- Add a user (an edit checks nothing more on the server) ---------------------------------------

export function userFormRules(v: { email?: string; name?: string; role?: string }, creating: boolean): Rule[] {
  if (!creating) return [];
  return [
    ["u-name", blank(v.name), "Enter the person's name."],
    ["u-email", !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v.email ?? "").trim().toLowerCase()), "Enter a valid email address."],
    ["u-role", !v.role, "Choose a role."],
  ];
}
